/* global EWG_REPORT_API_URL throttle */

/**
 * EWG Report Web Component
 */
class EWGReport extends HTMLElement {
  constructor() {
    super()
    this.initializeState()
    this.initializeDOMReferences()
    this.initializeResourceTrackers()
  }

  initializeState() {
    this.currentTab = 'exceeds'
    this.currentOption = null
    this.systemList = []
    this.dataCache = {}
  }

  initializeDOMReferences() {
    // 报告信息显示元素
    this.totalCount = null // 污染物总数显示（带动画效果）
    this.locationName = null // 位置名称显示（NodeList，可能有多个位置）
    this.population = null // 人口数显示
    this.source = null // 水源信息显示

    // 筛选相关元素
    this.filterOptions = null // 筛选选项容器（包含所有系统筛选按钮）
    this.moreButton = null // 展开/收起更多筛选选项按钮

    // Tab 切换元素
    this.tabs = null // Tab 标签列表（exceeds/others 切换）

    // 卡片轮播相关元素
    this.cardsSplideExceeds = null // 超过限值的污染物卡片轮播容器（Splide 实例）
    this.cardsSplideOthers = null // 其他污染物卡片轮播容器（Splide 实例）
    this.cardTemplate = null // 污染物卡片模板（用于克隆创建新卡片）
    this.cardsContainerExceeds = null // 超过限值的卡片容器（存放卡片 DOM）
    this.cardsContainerOthers = null // 其他污染物卡片容器（存放卡片 DOM）
    this.progressExceeds = null // 超过限值轮播的进度条
    this.progressOthers = null // 其他污染物轮播的进度条

    // 打印报告相关元素
    this.printReportModalOpener = null // 打开打印报告模态框的按钮
    this.printReportModal = null // 打印报告模态框容器
    this.printDownloadButton = null // 下载/打印报告按钮
    this.printClone = null // 打印时克隆的 DOM 节点（打印后需要清理）
  }

  initializeResourceTrackers() {
    this.sliderProgressHandlers = new Map()
    this.countAnimationFrame = null
    this.printTimeoutId = null
    this.eventListeners = new Map()
  }

  connectedCallback() {
    this.cacheDOMElements()
    this.setupEventListeners()
  }

  disconnectedCallback() {
    this.cleanupEventListeners()
    this.cleanupAnimations()
    this.cleanupTimers()
    this.cleanupSliderHandlers()
  }

  cacheDOMElements() {
    this.totalCount = this.querySelector('.ewg-report-number')
    this.locationName = this.querySelectorAll('.ewg-report-location-name')
    this.population = this.querySelector('.ewg-report-population')
    this.source = this.querySelector('.ewg-report-source')
    this.filterOptions = this.querySelector('.ewg-report-filters-options')
    this.moreButton = this.querySelector('.ewg-report-more')
    this.tabs = this.querySelectorAll('.ewg-report-tab')
    this.cardsSplideExceeds = this.querySelector('.ewg-report-cards-splide--exceeds')
    this.cardsSplideOthers = this.querySelector('.ewg-report-cards-splide--others')
    this.cardTemplate = this.querySelector('#ewg-report-card-template')
    this.cardsContainerExceeds = this.querySelector('#ewg-report-cards-container-exceeds')
    this.cardsContainerOthers = this.querySelector('#ewg-report-cards-container-others')
    this.progressExceeds = this.querySelector('#ewg-report-progress-bar-exceeds')
    this.progressOthers = this.querySelector('#ewg-report-progress-bar-others')
    this.printReportModalOpener = this.querySelector('.ewg-report-print-report-modal-opener')
    this.printReportModal = this.querySelector('.ewg-report-print-report-modal')
    this.printDownloadButton = this.printReportModal?.querySelector('#download-report-button')
  }

  setupEventListeners() {
    const searchParams = new URLSearchParams(window.location.search)
    let zip = (searchParams.get('zip') || '').trim()

    if (!zip) {
      zip = (window.localStorage.getItem('WD-ewg-zip-code') || '').trim()
    }

    // 添加 resize 监听器
    const resizeHandler = throttle(() => {
      this.checkMoreButtonVisibility()
    }, 300)
    window.addEventListener('resize', resizeHandler)
    this.trackEventListener(window, 'resize', resizeHandler)

    if (!zip) {
      this.showEmptyState(true)
      return
    }

    this.tabs?.forEach(tab => {
      const handler = this.handleTabClick.bind(this)
      tab.addEventListener('click', handler)
      this.trackEventListener(tab, 'click', handler)
    })

    if (this.printReportModalOpener) {
      const handler = this.showPrintReportModal.bind(this)
      this.printReportModalOpener.addEventListener('click', handler)
      this.trackEventListener(this.printReportModalOpener, 'click', handler)
    }

    if (this.printReportModal) {
      const closeButton = this.printReportModal.querySelector('.ewg-report-print-report-modal-close-button')
      if (closeButton) {
        const handler = this.hidePrintReportModal.bind(this)
        closeButton.addEventListener('click', handler)
        this.trackEventListener(closeButton, 'click', handler)
      }

      if (this.printDownloadButton) {
        const handler = this.downloadReport.bind(this)
        this.printDownloadButton.addEventListener('click', handler)
        this.trackEventListener(this.printDownloadButton, 'click', handler)
      }
    }

    const afterPrintHandler = this.handleAfterPrint.bind(this)
    window.addEventListener('afterprint', afterPrintHandler)
    this.trackEventListener(window, 'afterprint', afterPrintHandler)

    const showModal = searchParams.get('customer_posted') === 'true'
    this.loadReportData(zip, showModal)
    const socialButtons = this.querySelector('.ewg-report-button--link')
    if (socialButtons) socialButtons.dataset.value = window.location.href
  }

  trackEventListener(element, event, handler) {
    const key = `${element === window ? 'window' : element.constructor.name}-${event}`
    if (!this.eventListeners.has(key)) {
      this.eventListeners.set(key, [])
    }
    this.eventListeners.get(key).push({ element, event, handler })
  }

  cleanupEventListeners() {
    this.eventListeners.forEach(listeners => {
      listeners.forEach(({ element, event, handler }) => {
        element.removeEventListener(event, handler)
      })
    })
    this.eventListeners.clear()
  }

  cleanupAnimations() {
    if (this.countAnimationFrame) {
      cancelAnimationFrame(this.countAnimationFrame)
      this.countAnimationFrame = null
    }
  }

  cleanupTimers() {
    if (this.printTimeoutId) {
      clearTimeout(this.printTimeoutId)
      this.printTimeoutId = null
    }
  }

  cleanupSliderHandlers() {
    this.sliderProgressHandlers.forEach((handler, key) => {
      const splide = key === 'exceeds' ? this.cardsSplideExceeds : this.cardsSplideOthers
      if (!splide?.slider) return
      splide.slider.off('move refresh', handler)
    })
    this.sliderProgressHandlers.clear()
  }

  showEmptyState() {
    this.classList.add('empty')
    this.classList.remove('loading')
    this.ariaBusy = false
  }

  /**
   * 加载报告数据
   */
  async loadReportData(zip, showModal = false) {
    try {
      const systemList = await this.fetchSystemList(zip)
      if (!systemList?.length) {
        this.showEmptyState()
        return
      }

      this.systemList = systemList
      this.currentOption = systemList[0]

      await this.loadPWSInformation()
      this.updateSystemInfo()
      this.renderFilterButtons()
      if (showModal) this.showPrintReportModal()
    } catch {
      this.showEmptyState()
    }
  }

  /**
   * 获取系统列表
   */
  async fetchSystemList(zip) {
    const response = await fetch(`${EWG_REPORT_API_URL}/zip_systems?zip=${zip}`, { method: 'GET' })
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
    const data = await response.json()
    return data?.systemList
  }

  /**
   * 加载 PWS 详细信息
   */
  async loadPWSInformation() {
    if (!this.currentOption?.PWS) return

    const pws = this.currentOption.PWS
    const cacheEntry = this.getCachedData(pws)

    if (cacheEntry) {
      this.renderReportData(cacheEntry)
      return
    }

    this.setLoadingState(true)

    try {
      const information = await this.fetchPWSInformation(pws)
      if (!information) return

      this.cacheData(pws, information)
      this.renderReportData({ information })
    } catch {
      this.classList.add('empty')
    } finally {
      this.setLoadingState(false)
    }
  }

  /**
   * 获取缓存数据
   */
  getCachedData(pws) {
    const CACHE_DURATION = 1000 * 60 * 60 * 24
    const cached = this.dataCache[pws]
    if (cached && cached.timestamp > Date.now() - CACHE_DURATION) return cached
    return null
  }

  /**
   * 缓存数据
   */
  cacheData(pws, information) {
    this.dataCache[pws] = { information, timestamp: Date.now() }
  }

  /**
   * 获取 PWS 详细信息
   */
  async fetchPWSInformation(pws) {
    const response = await fetch(`${EWG_REPORT_API_URL}/information?pws=${pws}`, { method: 'GET' })
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
    const data = await response.json()
    return data?.information
  }

  /**
   * 设置加载状态
   */
  setLoadingState(isLoading) {
    if (isLoading) {
      this.classList.add('loading')
      this.ariaBusy = true
    } else {
      this.classList.remove('loading')
      this.ariaBusy = false
    }
  }

  /**
   * 渲染报告数据
   */
  renderReportData({ information }) {
    if (!information) return

    this.updateTotalCount(information.totalContaminantsCount || 0)
    this.updateLocationName(information.Systemname || '')

    if (information.exceedsList && information.othersList) {
      this.renderContaminantCards(information.exceedsList, 'exceeds')
      this.renderContaminantCards(information.othersList, 'others')
    }
  }

  /**
   * 更新总数显示（带动画）
   */
  updateTotalCount(count) {
    if (this.totalCount) {
      this.animateCount(this.totalCount, count)
    }
  }

  /**
   * 更新位置名称
   */
  updateLocationName(name) {
    if (this.locationName?.length) {
      this.locationName.forEach(element => {
        element.textContent = name
      })
    }
  }

  /**
   * 渲染污染物卡片列表
   */
  renderContaminantCards(list, tabType) {
    const config = this.getTabConfiguration(tabType)
    if (!list?.length || !this.cardTemplate || !config.container) {
      if (config.container) config.container.innerHTML = ''
      return
    }

    config.container.innerHTML = ''
    const fragment = this.createCardFragment(list)
    config.container.appendChild(fragment)
    this.setupSliderProgressTracking(config.splide, config.progress)
  }

  /**
   * 获取 Tab 配置
   */
  getTabConfiguration(tabType) {
    return {
      container: tabType === 'exceeds' ? this.cardsContainerExceeds : this.cardsContainerOthers,
      splide: tabType === 'exceeds' ? this.cardsSplideExceeds : this.cardsSplideOthers,
      progress: tabType === 'exceeds' ? this.progressExceeds : this.progressOthers
    }
  }

  /**
   * 创建卡片片段
   */
  createCardFragment(list) {
    const fragment = document.createDocumentFragment()
    let currentRow = null

    list.forEach((item, index) => {
      if (index % 2 === 0) {
        currentRow = document.createElement('li')
        currentRow.className = 'splide__slide ewg-report-cards-row'
        currentRow.setAttribute('data-row', Math.floor(index / 2))
        fragment.appendChild(currentRow)
      }

      const cardElement = this.createContaminantCard(item)
      if (currentRow && cardElement) currentRow.appendChild(cardElement)
    })

    return fragment
  }

  /**
   * 创建单个污染物卡片
   */
  createContaminantCard(item) {
    if (!item) return null

    const templateContent = this.cardTemplate.content.cloneNode(true)
    const cardElement = document.createElement('div')
    cardElement.className = 'ewg-report-card'

    while (templateContent.firstChild) {
      cardElement.appendChild(templateContent.firstChild)
    }

    this.populateCardData(cardElement, item)
    return cardElement
  }

  /**
   * 填充卡片数据
   */
  populateCardData(cardElement, item) {
    if (!cardElement || !item) return

    const elements = this.queryCardElements(cardElement)
    this.updateCardContent(elements, item)
  }

  /**
   * 查询卡片元素
   */
  queryCardElements(cardElement) {
    const selectors = {
      name: '.ewg-report-card-name',
      risk: '.ewg-report-card-risk-value',
      multiplier: '.ewg-report-card-multiplier',
      multiplierValue: '.ewg-report-card-multiplier-value',
      systemAverage: '[name="SystemAverage"]',
      systemAverageLabel: '[name="SystemAverageLabel"]',
      hgValue: '[name="ContaminantHGValue"]',
      hgValueLabel: '[name="ContaminantHGValueLabel"]',
      mclValue: '[name="ContaminantMCLValue"]',
      mclValueLabel: '[name="ContaminantMCLValueLabel"]'
    }

    const elements = {}
    Object.keys(selectors).forEach(key => {
      elements[key] = cardElement.querySelector(selectors[key])
    })
    return elements
  }

  /**
   * 更新卡片内容
   */
  updateCardContent(elements, item) {
    if (elements.name) elements.name.textContent = item.ContaminantName || ''

    if (elements.risk) elements.risk.textContent = item.ContaminantEffect || ''

    if (elements.multiplierValue && item.contaminantOverLimitRatio) {
      elements.multiplierValue.textContent = `${item.contaminantOverLimitRatio}X`
    } else if (elements.multiplier) {
      elements.multiplier.classList.add('hidden')
      elements.multiplier.ariaHidden = true
    }

    const unit = item.ContaminantUnits || ''
    this.updateDetailRow(elements.systemAverage, elements.systemAverageLabel, item.SystemAverage, unit)
    this.updateDetailRow(elements.hgValue, elements.hgValueLabel, item.ContaminantHGValue, unit)
    this.updateDetailRow(elements.mclValue, elements.mclValueLabel, item.ContaminantMCLValue, unit)
  }

  /**
   * 更新详情行
   */
  updateDetailRow(valueElement, labelElement, value, unit) {
    if (!valueElement || !labelElement) return

    if (value && value > 0) {
      valueElement.textContent = `${value} ${unit}`
      labelElement.classList.remove('empty')
    } else {
      valueElement.textContent = ''
      labelElement.classList.add('empty')
      labelElement.textContent = `No ${labelElement.textContent}`
    }
  }

  /**
   * 设置 Slider 进度跟踪
   */
  setupSliderProgressTracking(splide, progress) {
    if (!splide?.slider || !progress) return

    const { slider } = splide
    const handlerKey = splide === this.cardsSplideExceeds ? 'exceeds' : 'others'

    if (this.sliderProgressHandlers.has(handlerKey)) {
      slider.off('move refresh', this.sliderProgressHandlers.get(handlerKey))
    }

    const updateProgress = () => {
      const end = slider.Components.Controller.getEnd() + 1
      const rate = Math.min((slider.index + 1) / end, 1)
      progress.style.width = `${100 * rate}%`
    }

    this.sliderProgressHandlers.set(handlerKey, updateProgress)
    slider.on('move refresh', updateProgress)
    slider.refresh()
  }

  /**
   * 更新系统信息显示
   */
  updateSystemInfo() {
    if (!this.currentOption) return
    if (this.locationName) this.locationName.textContent = this.currentOption.SystemName || ''
    if (this.population) this.population.textContent = this.currentOption.Population || ''
    if (this.source) this.source.textContent = this.currentOption.PrimSource_fulltext || ''
  }

  /**
   * 渲染筛选按钮
   */
  renderFilterButtons() {
    if (!this.filterOptions || !this.systemList?.length) return

    const fragment = document.createDocumentFragment()
    this.systemList.forEach((system, index) => {
      if (!system.PWS || !system.SystemName) return
      const button = this.createFilterButton(system, index)
      fragment.appendChild(button)
    })

    this.filterOptions.appendChild(fragment)
    this.checkMoreButtonVisibility()

    if (this.moreButton && !this.moreButton.dataset.listener) {
      this.moreButton.dataset.listener = 'true'
      const handler = this.toggleMoreOptions.bind(this)
      this.moreButton.addEventListener('click', handler)
      this.trackEventListener(this.moreButton, 'click', handler)
    }
  }

  /**
   * 创建筛选按钮
   */
  createFilterButton(system, index) {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = `ewg-report-filter-button p2-s${index === 0 ? ' ewg-report-filter-button--active' : ''}`
    button.dataset.filter = system.PWS
    button.dataset.systemName = system.SystemName
    button.textContent = system.SystemName

    const handler = throttle(() => {
      this.handleFilterClick(button, system)
    }, 300)

    button.addEventListener('click', handler)
    this.trackEventListener(button, 'click', handler)
    return button
  }

  /**
   * 检查是否需要显示 "More" 按钮
   */
  checkMoreButtonVisibility() {
    if (!this.filterOptions || !this.moreButton) return

    this.filterOptions.classList.remove('expanded')
    this.moreButton.classList.remove('show', 'ewg-report-more-expanded')

    requestAnimationFrame(() => {
      if (this.filterOptions.scrollHeight > 40) this.moreButton.classList.add('show')
    })
  }

  /**
   * 切换更多选项显示
   */
  toggleMoreOptions() {
    if (!this.filterOptions || !this.moreButton) return

    const isExpanded = this.moreButton.classList.contains('ewg-report-more-expanded')
    this.moreButton.classList.toggle('ewg-report-more-expanded', !isExpanded)
    this.moreButton.ariaLabel = isExpanded ? 'More' : 'Less'

    if (isExpanded) this.filterOptions.style.removeProperty('height')
    else this.filterOptions.style.setProperty('height', `${this.filterOptions.scrollHeight}px`)
  }

  /**
   * 处理筛选按钮点击
   */
  async handleFilterClick(button, system) {
    if (button.classList.contains('ewg-report-filter-button--active') || this.filterOptions.children.length <= 1) {
      return
    }

    this.currentOption = system
    this.updateFilterButtonStates(button)
    this.updateSystemInfo()
    this.switchTab('exceeds')
    await this.loadPWSInformation()
  }

  /**
   * 更新筛选按钮状态
   */
  updateFilterButtonStates(activeButton) {
    Array.from(this.filterOptions.children).forEach(btn => {
      btn.classList.toggle('ewg-report-filter-button--active', btn === activeButton)
    })
  }

  /**
   * 处理 Tab 点击
   */
  handleTabClick(e) {
    const tab = e.target.closest('.ewg-report-tab')
    if (!tab) return
    const tabName = tab.dataset.tab
    if (tabName && tabName !== this.currentTab) this.switchTab(tabName)
  }

  /**
   * 切换 Tab
   */
  switchTab(tabName) {
    if (tabName === this.currentTab || !this.cardsSplideExceeds || !this.cardsSplideOthers) return

    this.tabs.forEach(tab => {
      tab.classList.toggle('ewg-report-tab--active', tab.dataset.tab === tabName)
    })

    const isExceeds = tabName === 'exceeds'
    this.cardsSplideExceeds.classList.toggle('active', isExceeds)
    this.cardsSplideExceeds.ariaSelected = isExceeds
    this.cardsSplideOthers.classList.toggle('active', !isExceeds)
    this.cardsSplideOthers.ariaSelected = !isExceeds
    this.currentTab = tabName
  }

  /**
   * 显示打印报告模态框
   */
  showPrintReportModal() {
    if (!this.printReportModal) return
    this.printReportModal.classList.add('active')
  }

  /**
   * 隐藏打印报告模态框
   */
  hidePrintReportModal() {
    if (!this.printReportModal) return
    this.printReportModal.classList.remove('active')
  }

  /**
   * 处理打印后事件
   */
  handleAfterPrint() {
    this.printDownloadButton?.classList.remove('loading')
    if (this.printClone && this.printClone.parentNode) {
      this.printClone.parentNode.removeChild(this.printClone)
      this.printClone = null
    }
  }

  /**
   * 下载/打印报告
   */
  async downloadReport() {
    if (!this.printReportModal || !this.currentOption?.PWS) return

    this.printDownloadButton.classList.add('loading')
    const printContainer = document.querySelector('.ewg-report-print')
    if (!printContainer) return

    const clone = printContainer.cloneNode(true)
    this.populatePrintData(clone)

    document.body.appendChild(clone)
    this.printClone = clone
    this.printTimeoutId = setTimeout(() => {
      window.print()
      this.printTimeoutId = null
    }, 500)
  }

  /**
   * 填充打印数据
   */
  populatePrintData(clone) {
    const selectors = {
      title: '.ewg-report-print-title-name',
      name: '.ewg-report-print-location-name',
      number: '.ewg-report-print-number',
      numberTotal: '.ewg-report-print-number-total',
      population: '.ewg-report-print-population',
      source: '.ewg-report-print-source',
      exceedsBlock: '#ewg-report-print-exceeds-block',
      othersBlock: '#ewg-report-print-others-block'
    }

    const elements = {}
    Object.keys(selectors).forEach(key => {
      elements[key] = clone.querySelector(selectors[key])
    })

    const cacheEntry = this.dataCache[this.currentOption.PWS]
    if (!cacheEntry?.information) return

    const { information } = cacheEntry

    if (elements.title) elements.title.textContent = this.currentOption.SystemName
    if (elements.name) elements.name.textContent = this.currentOption.SystemName
    if (elements.number) elements.number.textContent = information.totalContaminantsCount
    if (elements.numberTotal) elements.numberTotal.textContent = information.totalAllCount
    if (elements.population) elements.population.textContent = this.currentOption.Population
    if (elements.source) elements.source.textContent = this.currentOption.PrimSource_fulltext

    const printCardTemplate = clone.querySelector('#ewg-report-print-card-template')
    if (printCardTemplate) {
      this.populatePrintCards(printCardTemplate, elements, information)
    }
  }

  /**
   * 填充打印卡片
   */
  populatePrintCards(template, elements, information) {
    if (information.exceedsList?.length && elements.exceedsBlock) {
      elements.exceedsBlock.innerHTML = ''
      information.exceedsList.forEach(item => {
        const card = this.createPrintCard(template, item)
        if (card) elements.exceedsBlock.appendChild(card)
      })
    }

    if (information.othersList?.length && elements.othersBlock) {
      elements.othersBlock.innerHTML = ''
      information.othersList.forEach(item => {
        const card = this.createPrintCard(template, item)
        if (card) elements.othersBlock.appendChild(card)
      })
    }
  }

  /**
   * 创建打印卡片
   */
  createPrintCard(template, item) {
    if (!template || !item) return null

    const templateContent = template.content.cloneNode(true)
    const cardElement = templateContent.querySelector('.ewg-report-print-card')
    if (!cardElement) return null

    const nameElement = cardElement.querySelector('.ewg-report-print-card-name')
    const riskElement = cardElement.querySelector('.ewg-report-print-card-risk')
    const systemAverageElement = cardElement.querySelector('#print-SystemAverage')
    const systemAverageLabelElement = cardElement.querySelector('#print-SystemAverageLabel')
    const hgValueElement = cardElement.querySelector('#print-ContaminantHGValue')
    const hgValueLabelElement = cardElement.querySelector('#print-ContaminantHGValueLabel')
    const mclValueElement = cardElement.querySelector('#print-ContaminantMCLValue')
    const mclValueLabelElement = cardElement.querySelector('#print-ContaminantMCLValueLabel')

    if (nameElement) nameElement.textContent = item.ContaminantName || ''
    if (riskElement) riskElement.textContent = `Health Risk: ${item.ContaminantEffect}`

    const unit = item.ContaminantUnits || ''
    this.updateDetailRow(systemAverageElement, systemAverageLabelElement, item.SystemAverage, unit)
    this.updateDetailRow(hgValueElement, hgValueLabelElement, item.ContaminantHGValue, unit)
    this.updateDetailRow(mclValueElement, mclValueLabelElement, item.ContaminantMCLValue, unit)

    return cardElement
  }

  /**
   * 数字递增动画
   */
  animateCount(element, targetValue, duration = 1500) {
    if (!element) return

    const startValue = 0
    const startTime = performance.now()
    const endTime = startTime + duration

    const animate = currentTime => {
      if (currentTime >= endTime) {
        element.textContent = targetValue
        this.countAnimationFrame = null
        return
      }

      const progress = Math.min((currentTime - startTime) / duration, 1)
      const easeOutQuart = 1 - Math.pow(1 - progress, 4)
      const currentValue = Math.floor(startValue + (targetValue - startValue) * easeOutQuart)

      element.textContent = currentValue
      this.countAnimationFrame = requestAnimationFrame(animate)
    }

    if (this.countAnimationFrame) {
      cancelAnimationFrame(this.countAnimationFrame)
    }
    this.countAnimationFrame = requestAnimationFrame(animate)
  }
}

if (!customElements.get('ewg-report')) {
  customElements.define('ewg-report', EWGReport)
}

/**
 * EWG Report Enter 组件
 */
class EWGReportEnter extends HTMLElement {
  constructor() {
    super()
    this.zipInput = null
    this.inputWrapper = null
    this.boundHandlers = {
      onKeyDown: null,
      onInput: null
    }
  }

  connectedCallback() {
    this.initializeElements()
    this.attachEventListeners()
  }

  disconnectedCallback() {
    this.detachEventListeners()
  }

  /**
   * 初始化 DOM 元素引用
   */
  initializeElements() {
    this.zipInput = this.querySelector('.ewg-report-enter__input')
    this.inputWrapper = this.zipInput?.closest('.ewg-report-enter__input-wrapper')
  }

  /**
   * 绑定事件监听器
   */
  attachEventListeners() {
    if (!this.zipInput) return

    this.boundHandlers.onKeyDown = this.handleKeyDown.bind(this)
    this.boundHandlers.onInput = this.handleInput.bind(this)

    this.zipInput.addEventListener('keydown', this.boundHandlers.onKeyDown)
    this.zipInput.addEventListener('input', this.boundHandlers.onInput)
  }

  /**
   * 移除事件监听器
   */
  detachEventListeners() {
    if (!this.zipInput) return

    this.zipInput.removeEventListener('keydown', this.boundHandlers.onKeyDown)
    this.zipInput.removeEventListener('input', this.boundHandlers.onInput)

    this.boundHandlers.onKeyDown = null
    this.boundHandlers.onInput = null
  }

  isValidZipCode(value) {
    const zipRegex = /^\d{5}(-\d{4})?$/
    return zipRegex.test(value.trim())
  }

  handleInput(event) {
    const value = event.target.value.trim()
    const isValid = this.isValidZipCode(value)

    if (this.inputWrapper) {
      this.inputWrapper.classList.toggle('error', !isValid && value !== '')
    }

    event.target.setAttribute('aria-invalid', !isValid && value !== '' ? 'true' : 'false')
  }

  handleKeyDown(event) {
    if (event.key !== 'Enter') return

    event.preventDefault()

    const value = event.target.value.trim()
    if (!this.isValidZipCode(value)) return

    window.localStorage.setItem('WD-ewg-zip-code', value)
    this.navigateToResult(value)
  }

  /**
   * 跳转到结果页
   */
  navigateToResult(zipCode) {
    const { resultUrl = window.location.href } = this.dataset

    const url = new URL(resultUrl)
    url.searchParams.set('zip', zipCode)
    window.location.href = url.toString()
  }
}

if (!customElements.get('ewg-report-enter')) {
  customElements.define('ewg-report-enter', EWGReportEnter)
}
